import React, { useState } from 'react';
import { Controller, Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import {
  Grid,
  Chip,
  TextField,
  Switch,
  FormControlLabel as MuiFormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Check as CheckIcon } from '@mui/icons-material';
import { ChallengeStatus } from 'generated/graphql';
import { ChallengeFormData, TeamOption } from '../types';
import { labelStyle } from '../utils';

interface TeamsStepProps {
  control: Control<ChallengeFormData>;
  teams: TeamOption[];
  selectedTeams: TeamOption[];
  setSelectedTeams: (teams: TeamOption[]) => void;
  setValue: UseFormSetValue<ChallengeFormData>;
  watch: UseFormWatch<ChallengeFormData>;
  challengeStatus?: ChallengeStatus
  startDate?: string;
  endDate?: string;
}

const isChallengeActive = (
  status?: ChallengeStatus,
  startDate?: string,
  endDate?: string
): boolean => {
  if (status !== ChallengeStatus.Published) {
    return false;
  }

  if (!startDate || !endDate) {
    return false;
  }

  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  return now >= start && now <= end;
};

const isChallengePast = (
  status?: ChallengeStatus,
  endDate?: string
): boolean => {
  if (status !== ChallengeStatus.Published || !endDate) {
    return false;
  }

  const now = new Date();
  const end = new Date(endDate);

  return now > end;
};

export const TeamsStep = ({
  control,
  teams,
  selectedTeams,
  setSelectedTeams,
  setValue,
  watch,
  challengeStatus,
  startDate,
  endDate,
}: TeamsStepProps) => {

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pastChallengeDialogOpen, setPastChallengeDialogOpen] = useState(false);
  const [teamToRemove, setTeamToRemove] = useState<TeamOption | null>(null);
  const [pendingTeamChanges, setPendingTeamChanges] = useState<TeamOption[] | null>(null);

  const isActive = isChallengeActive(challengeStatus, startDate, endDate);
  const isPast = isChallengePast(challengeStatus, endDate);
  const isExcludeMode = watch('teams.isExclude');

  const handleFieldClick = () => {
    if (isPast) {
      setPastChallengeDialogOpen(true);
    }
  };

  const handleChipDelete = (teamToDelete: TeamOption) => {
    // In include mode: removing a chip means removing the team from the challenge
    // In exclude mode: removing a chip means allowing the team back in (not removing)
    const isRemovingFromChallenge = !isExcludeMode;

  if (isRemovingFromChallenge && isActive) {
    setTeamToRemove(teamToDelete);
    setConfirmDialogOpen(true);
  } else {
    const newTeams = selectedTeams.filter((team) => team.id !== teamToDelete.id);
    setSelectedTeams(newTeams);
    setValue(
      'teams.ids',
      newTeams.map((team) => team.id)
    );
  }
};

  const handleTeamChange = (newValue: TeamOption[]) => {
    // In exclude mode: adding a team means excluding them from the challenge
    const isAddingTeams = newValue.length > selectedTeams.length;
    const isExcludingTeams = isExcludeMode && isAddingTeams;

    if (isExcludingTeams && isActive) {
      // Store the pending changes and show confirmation dialog
      setPendingTeamChanges(newValue);
      setConfirmDialogOpen(true);
    } else {
      setSelectedTeams(newValue);
      setValue(
        'teams.ids',
        newValue.map((team) => team.id)
      );
    }
  };

  const handleConfirmRemoval = () => {
    if (teamToRemove) {
      // Removing a team in include mode
      const newTeams = selectedTeams.filter((team) => team.id !== teamToRemove.id);
      
      setSelectedTeams(newTeams);
      setValue(
        'teams.ids',
        newTeams.map((team) => team.id)
      );
    } else if (pendingTeamChanges) {
      // Adding teams in exclude mode
      setSelectedTeams(pendingTeamChanges);
      setValue(
        'teams.ids',
        pendingTeamChanges.map((team) => team.id)
      );
    }
    setConfirmDialogOpen(false);
    setTeamToRemove(null);
    setPendingTeamChanges(null);
  };

  const handleCancelRemoval = () => {
    setConfirmDialogOpen(false);
    setTeamToRemove(null);
    setPendingTeamChanges(null);
  };


  return (
    <div>
      <div style={{ marginBottom: 40 }}>
        <p style={{ marginBottom: 5 }}>Participating Teams</p>
        <p style={{ ...labelStyle, marginBottom: 20 }}>
          Select which teams can participate in this challenge. You can include or exclude specific
          teams. If You chose "Exclude selected teams" and don't pick any team, it means all teams
          will participate.
        </p>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Autocomplete<TeamOption, true>
              multiple
              options={teams}
              disableCloseOnSelect
              getOptionLabel={(option) => option.title}
              value={selectedTeams}
              onChange={(_, newValue) => handleTeamChange(newValue)}
              disabled={isPast}
              renderOption={(option, { selected }) => (
                <React.Fragment>
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between', padding: '8px 0' }}>
                    <span style={{ fontSize: '16px' }}>{option.title}</span>
                    {selected && (
                      <CheckIcon style={{ color: '#6366F1', fontSize: '20px' }} />
                    )}
                  </div>
                </React.Fragment>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {

                  const tagProps = getTagProps({ index }) as any;
                  const { onDelete, ...otherTagProps } = tagProps;

                  return (
                  <Chip
                    variant="outlined"
                    label={option.title}
                    {...otherTagProps}
                    key={option.id}
                    onDelete={!isPast ? () => handleChipDelete(option) : undefined}
                    style={{
                      backgroundColor: '#F3F4F6',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      fontSize: '14px',
                      height: '32px',
                    }}
                  />
                  )
              })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Teams"
                  variant="outlined"
                  onClick={handleFieldClick}
                  placeholder={watch('teams.isExclude')
                    ? 'Teams to be excluded from the challenge'
                    : 'Teams to be included in the challenge'}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <div style={{ marginTop: 5, marginBottom: 10 }}>
              <Controller
                name="teams.isExclude"
                control={control}
                render={({ field }) => (
                  <MuiFormControlLabel
                  control={
                    <Switch 
                      checked={field.value} 
                      onChange={field.onChange} 
                      disabled={isPast}
                    />
                  }
                    label="Exclude selected teams"
                    onClick={handleFieldClick}
                  />
                )}
              />
            </div>
          </Grid>
        </Grid>
      </div>
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelRemoval}
        aria-labelledby="confirm-team-removal-title"
        aria-describedby="confirm-team-removal-description"
      >
       <DialogTitle id="confirm-team-removal-title">
          {isExcludeMode ? 'Exclude Team from Challenge' : 'Remove Team from Challenge'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-team-removal-description">
            {isExcludeMode
              ? 'When you exclude the team, all their contributions won\'t be counted in the challenge.'
              : 'When you remove the team, all their contributions won\'t be counted in the challenge.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemoval} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmRemoval} color="primary" autoFocus>
            {isExcludeMode ? 'Exclude' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pastChallengeDialogOpen}
        onClose={() => setPastChallengeDialogOpen(false)}
        aria-labelledby="past-challenge-title"
        aria-describedby="past-challenge-description"
      >
        <DialogTitle id="past-challenge-title">Action Blocked</DialogTitle>
        <DialogContent>
          <DialogContentText id="past-challenge-description">
            Teams cannot be modified after a challenge has ended.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPastChallengeDialogOpen(false)} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TeamsStep;